// eslint-disable-next-line import/no-unresolved -- Vite virtual module
import tourDefinitions from 'virtual:guide-tour-definitions'

import {
  applyGuideTourRuntimeConfig,
  defineGuideTourConfig,
  type GuideTourConfigInput,
} from './config'
import type { GuideTourDefinition, GuideTourRegistry } from './types'
import {
  GuideTourValidationError,
  validateGuideTourDefinition,
  validateGuideTourRegistry,
} from './validation'

export const buildGuideTourRegistry = (
  definitions: readonly GuideTourDefinition[]
): GuideTourRegistry => {
  validateGuideTourRegistry(definitions)

  const toursById = new Map(
    definitions.map(definition => [definition.id, definition])
  )

  return {
    getGuideTour: (id: string) => toursById.get(id),
    listGuideTours: () => [...definitions],
  }
}

export type GuideTourRegistrySetup = {
  guideTourDefinitions: readonly GuideTourDefinition[]
  registry: GuideTourRegistry
  getGuideTour: (id: string) => GuideTourDefinition | undefined
}

const parseGuideTourDefinition = (
  sourcePath: string,
  sourceValue: unknown
): GuideTourDefinition => {
  if (!sourcePath.toLowerCase().endsWith('.json')) {
    throw new GuideTourValidationError(
      `Tour config must be a .json file: ${sourcePath}`
    )
  }

  if (
    sourceValue === null ||
    typeof sourceValue !== 'object' ||
    Array.isArray(sourceValue)
  ) {
    throw new GuideTourValidationError(
      `Tour config must be a JSON object: ${sourcePath}`
    )
  }

  const definition = sourceValue as GuideTourDefinition

  try {
    validateGuideTourDefinition(definition)
  } catch (error) {
    if (error instanceof GuideTourValidationError) {
      throw new GuideTourValidationError(`${sourcePath}: ${error.message}`)
    }

    throw error
  }

  return definition
}

export const parseGuideTourDefinitions = (
  sources: Record<string, unknown>
): GuideTourDefinition[] =>
  Object.entries(sources)
    .map(([sourcePath, sourceValue]) =>
      parseGuideTourDefinition(sourcePath, sourceValue)
    )
    .sort((left, right) => left.id.localeCompare(right.id))

export const setupGuideTourRegistry = (
  sources: Record<string, unknown>
): GuideTourRegistrySetup => {
  const guideTourDefinitions = parseGuideTourDefinitions(sources)
  const registry = buildGuideTourRegistry(guideTourDefinitions)

  return {
    guideTourDefinitions,
    registry,
    getGuideTour: (id: string) => registry.getGuideTour(id),
  }
}

export type CreateGuideTourRegistryOptions = {
  /**
   * When `true`, resolves `config` and calls `applyGuideTourRuntimeConfig`.
   * Host apps should prefer applying config explicitly (see `src/tours/index.ts`).
   */
  applyRuntimeConfig?: boolean
}

export const createGuideTourRegistry = (
  config: GuideTourConfigInput = {},
  options: CreateGuideTourRegistryOptions = {}
): GuideTourRegistrySetup => {
  if (options.applyRuntimeConfig === true) {
    applyGuideTourRuntimeConfig(defineGuideTourConfig(config))
  }

  return setupGuideTourRegistry(tourDefinitions)
}
